import { FIREWALL_ZONES } from '@connaro/shared';
import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { Banner, EmptyState, ErrorState, Loading } from './states';

interface FirewallRule {
  id: string;
  priority: number;
  description: string;
  sourceCidr: string | null;
  destCidr: string | null;
  sourceZone: string | null;
  destZone: string | null;
  protocol: string;
  destPort: number | null;
  action: string;
  rateLimitPerSecond: number | null;
  log: boolean;
  mark: number | null;
  dscp: number | null;
  enabled: boolean;
}

const ACTION_CHIP: Record<string, string> = {
  ALLOW: 'chip--success',
  DENY: 'chip--warning',
  REJECT: 'chip--failure',
};

const emptyForm = {
  priority: '100',
  sourceCidr: '',
  destCidr: '',
  sourceZone: '',
  destZone: '',
  protocol: 'ANY',
  destPort: '',
  action: 'ALLOW',
  rateLimit: '',
  log: false,
  mark: '',
  dscp: '',
  description: '',
};

/** A rule's source or destination, shown as its zone, its prefix, or "any". */
function selectorLabel(zone: string | null, cidr: string | null, any: string): string {
  if (zone && cidr) return `${zone} + ${cidr}`;
  return zone ?? cidr ?? any;
}

/** A compact summary of a rule's extended actions, e.g. "log · rl20 · mark7 · dscp46". */
function modifierLabel(rule: FirewallRule): string {
  const parts: string[] = [];
  if (rule.log) parts.push('log');
  if (rule.rateLimitPerSecond !== null) parts.push(`rl${rule.rateLimitPerSecond}`);
  if (rule.mark !== null) parts.push(`mark${rule.mark}`);
  if (rule.dscp !== null) parts.push(`dscp${rule.dscp}`);
  return parts.join(' · ');
}

/**
 * Firewall rules for one edge, and a preview of the ruleset they render into.
 *
 * The rules are what an operator sets; the safety around them — the default deny,
 * the management reachability — is added by the renderer, so the preview shows the
 * ruleset that will actually run, not just the rules typed in. Rendering happens on
 * the controller, so what is shown here is exactly what the edge will apply.
 */
export function FirewallPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<FirewallRule[]>(`/firewall/devices/${deviceId}/rules`),
    [deviceId],
  );
  const [form, setForm] = useState({ ...emptyForm });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [rendered, setRendered] = useState<string | null>(null);

  const set = (k: keyof typeof emptyForm, v: string): void => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitError(null);
    try {
      await api.post(`/firewall/devices/${deviceId}/rules`, {
        priority: Number(form.priority),
        protocol: form.protocol,
        action: form.action,
        ...(form.sourceCidr ? { sourceCidr: form.sourceCidr } : {}),
        ...(form.destCidr ? { destCidr: form.destCidr } : {}),
        ...(form.sourceZone ? { sourceZone: form.sourceZone } : {}),
        ...(form.destZone ? { destZone: form.destZone } : {}),
        ...(form.destPort ? { destPort: Number(form.destPort) } : {}),
        ...(form.rateLimit ? { rateLimitPerSecond: Number(form.rateLimit) } : {}),
        ...(form.log ? { log: true } : {}),
        ...(form.mark ? { mark: Number(form.mark) } : {}),
        ...(form.dscp ? { dscp: Number(form.dscp) } : {}),
        ...(form.description ? { description: form.description } : {}),
      });
      setForm({ ...emptyForm });
      setShowForm(false);
      if (rendered !== null) await loadRendered();
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    }
  };

  const remove = async (id: string): Promise<void> => {
    if (!window.confirm(t('firewall.confirmDelete'))) return;
    await api.del(`/firewall/rules/${id}`);
    if (rendered !== null) await loadRendered();
    reload();
  };

  const loadRendered = async (): Promise<void> => {
    const res = await api.get<{ ruleset: string }>(`/firewall/devices/${deviceId}/rendered`);
    setRendered(res.ruleset);
  };

  const toggleRendered = (): void => {
    if (rendered === null) void loadRendered();
    else setRendered(null);
  };

  return (
    <div className="card">
      <div className="page-head">
        <h2>{t('firewall.title')}</h2>
        <button type="button" className="btn" onClick={() => setShowForm((s) => !s)}>
          {showForm ? t('common.cancel') : t('firewall.addRule')}
        </button>
      </div>

      {showForm ? (
        <form className="card form" onSubmit={(e) => void submit(e)}>
          {submitError ? <Banner variant="error">{submitError}</Banner> : null}
          <label className="field">
            <span>{t('firewall.priority')}</span>
            <input
              type="number"
              value={form.priority}
              onChange={(e) => set('priority', e.target.value)}
            />
          </label>
          <label className="field">
            <span>{t('firewall.sourceZone')}</span>
            <select value={form.sourceZone} onChange={(e) => set('sourceZone', e.target.value)}>
              <option value="">{t('firewall.any')}</option>
              {FIREWALL_ZONES.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('firewall.source')}</span>
            <input
              placeholder="10.1.0.0/24"
              value={form.sourceCidr}
              onChange={(e) => set('sourceCidr', e.target.value)}
            />
          </label>
          <label className="field">
            <span>{t('firewall.destZone')}</span>
            <select value={form.destZone} onChange={(e) => set('destZone', e.target.value)}>
              <option value="">{t('firewall.any')}</option>
              {FIREWALL_ZONES.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('firewall.destination')}</span>
            <input
              placeholder="10.2.0.0/24"
              value={form.destCidr}
              onChange={(e) => set('destCidr', e.target.value)}
            />
          </label>
          <label className="field">
            <span>{t('firewall.protocol')}</span>
            <select value={form.protocol} onChange={(e) => set('protocol', e.target.value)}>
              {['ANY', 'TCP', 'UDP', 'ICMP'].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('firewall.port')}</span>
            <input
              type="number"
              placeholder="443"
              value={form.destPort}
              onChange={(e) => set('destPort', e.target.value)}
            />
          </label>
          <label className="field">
            <span>{t('firewall.action')}</span>
            <select value={form.action} onChange={(e) => set('action', e.target.value)}>
              <option value="ALLOW">{t('firewall.allow')}</option>
              <option value="DENY">{t('firewall.deny')}</option>
              <option value="REJECT">{t('firewall.reject')}</option>
            </select>
          </label>
          <label className="field">
            <span>{t('firewall.rateLimit')}</span>
            <input
              type="number"
              placeholder="20"
              value={form.rateLimit}
              onChange={(e) => set('rateLimit', e.target.value)}
            />
          </label>
          <label className="field">
            <span>{t('firewall.mark')}</span>
            <input type="number" value={form.mark} onChange={(e) => set('mark', e.target.value)} />
          </label>
          <label className="field">
            <span>{t('firewall.dscp')}</span>
            <input type="number" value={form.dscp} onChange={(e) => set('dscp', e.target.value)} />
          </label>
          <label className="field field--checkbox">
            <input
              type="checkbox"
              checked={form.log}
              onChange={(e) => setForm((f) => ({ ...f, log: e.target.checked }))}
            />
            <span>{t('firewall.log')}</span>
          </label>
          <label className="field">
            <span>{t('firewall.description')}</span>
            <input value={form.description} onChange={(e) => set('description', e.target.value)} />
          </label>
          <button type="submit" className="btn">
            {t('common.save')}
          </button>
        </form>
      ) : null}

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error.message} onRetry={reload} />
      ) : !data || data.length === 0 ? (
        <EmptyState hint={t('firewall.noRules')} />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('firewall.priority')}</th>
                <th>{t('firewall.source')}</th>
                <th>{t('firewall.destination')}</th>
                <th>{t('firewall.protocol')}</th>
                <th>{t('firewall.port')}</th>
                <th>{t('firewall.action')}</th>
                <th>{t('firewall.modifiers')}</th>
                <th aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {data.map((rule) => (
                <tr key={rule.id}>
                  <td>{rule.priority}</td>
                  <td>{selectorLabel(rule.sourceZone, rule.sourceCidr, t('firewall.any'))}</td>
                  <td>{selectorLabel(rule.destZone, rule.destCidr, t('firewall.any'))}</td>
                  <td>{rule.protocol}</td>
                  <td>{rule.destPort ?? t('firewall.any')}</td>
                  <td>
                    <span className={`chip ${ACTION_CHIP[rule.action] ?? 'chip--warning'}`}>
                      {rule.action}
                    </span>
                  </td>
                  <td className="muted">{modifierLabel(rule) || '—'}</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn--ghost btn--danger"
                      onClick={() => void remove(rule.id)}
                    >
                      {t('firewall.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="page-head">
        <button type="button" className="btn btn--ghost" onClick={toggleRendered}>
          {rendered === null ? t('firewall.showRendered') : t('firewall.hideRendered')}
        </button>
      </div>
      {rendered !== null ? <pre className="code-block">{rendered}</pre> : null}
    </div>
  );
}
